import React from 'react';
import { ArrowRight, Code2, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Animated floating background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-blue-400 to-purple-400 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full filter blur-3xl opacity-30"
          animate={{ 
            y: [0, 50, 0],
            x: [0, 30, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-r from-cyan-400 to-blue-500 dark:from-cyan-900/30 dark:to-blue-900/30 rounded-full filter blur-3xl opacity-30"
          animate={{ 
            y: [0, -50, 0],
            x: [0, -30, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 25, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 1
          }}
        />
        <motion.div 
          className="absolute top-1/3 right-1/4 w-72 h-72 bg-gradient-to-r from-purple-400 to-pink-400 dark:from-purple-900/20 dark:to-pink-900/20 rounded-full filter blur-3xl opacity-20"
          animate={{ 
            y: [0, -40, 0],
            x: [0, 40, 0],
            scale: [1, 1.15, 1]
          }}
          transition={{ 
            duration: 18, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Badge */}
        <motion.div 
          className="inline-flex items-center space-x-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg text-blue-700 dark:text-blue-300 px-5 py-2.5 rounded-full mb-8 border border-blue-200 dark:border-blue-800 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Sparkles className="w-4 h-4 animate-pulse" />
          <span className="text-sm font-semibold">Your Friendly Tech Partner</span>
        </motion.div>

        {/* Main heading */}
        <motion.h1 
          className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <span className="block">Welcome to</span>
          <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
            Code Mitra
          </span>
        </motion.h1>

        {/* Subheading */}
        <motion.p 
          className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          Transforming ideas into exceptional digital experiences. We build modern web and mobile applications that drive growth and innovation.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div 
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
            <Button
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group"
            >
              <span className="relative z-10 flex items-center">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
            <Button
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('projects')}
              className="px-8 py-6 text-lg rounded-xl border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-800 backdrop-blur-lg bg-white/50 dark:bg-gray-900/50 transition-all duration-300 hover:shadow-lg"
            >
              View Projects
            </Button>
          </motion.div>
        </motion.div>

        {/* Tech Icons */}
        <motion.div 
          className="flex items-center justify-center space-x-8 text-gray-400 dark:text-gray-600"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.8 }}
        >
          <motion.div whileHover={{ scale: 1.2, color: "#2563eb" }} transition={{ duration: 0.3 }}>
            <Code2 className="w-8 h-8 cursor-pointer" />
          </motion.div>
          <div className="w-px h-8 bg-gray-300 dark:bg-gray-700"></div>
          <motion.span whileHover={{ scale: 1.1 }} className="text-sm font-medium cursor-default">React</motion.span>
          <motion.span whileHover={{ scale: 1.1 }} className="text-sm font-medium cursor-default">Node.js</motion.span>
          <motion.span whileHover={{ scale: 1.1 }} className="text-sm font-medium cursor-default">MongoDB</motion.span>
          <motion.span whileHover={{ scale: 1.1 }} className="text-sm font-medium cursor-default">Cloud</motion.span>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-gray-400 dark:border-gray-600 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-gray-400 dark:bg-gray-600 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
